import { Component, Inject } from '@angular/core';
import { EventEmitter, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/services/api.service';
import { JwtService } from 'src/app/services/jwt.service';
import { MixpanelService } from 'src/app/services/mixpanel.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { UploadDialog } from '../../upload-dailog.component';
import { Router } from '@angular/router';
import {
  MatBottomSheet,
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet';

@Component({
  selector: 'uploaddocument',
  templateUrl: './uploaddocument.component.html',
  styleUrls: ['./uploaddocument.component.css'],
})
export class UploaddocumentComponent {
  selectedFile: File | null = null;
  loaded = false;
  buttonClicked = false;
  success = false;
  status: string;
  tableHeaders: string[] = [];
  documentarchives = {
    client: null,
    year: null,
    docType: null,
    reportType: null,
    reportPeriod: null,
    to: null,
  };

  collateralsForm: FormGroup;
  uploadedFile: File | null = null;
  clients = JSON.parse(sessionStorage.getItem('clients')) || [];
  to: string[] = [];
  docTypes: string[] = [];
  reportTypes: string[] = [];
  reportPeriod: string[] = [];
  year: string[] = [];
  dropdownOptions = {
    to: [] as string[],
    docType: [] as string[],
    reportType: [] as string[],
    reportPeriod: [] as string[],
    year: [] as string[],
    quarterDropdown: [] as string[],
    monthlyDropdown: [] as string[],
  };

  constructor(
    private api: ApiService,
    private snackbar: MatSnackBar,
    private _bottomSheet: MatBottomSheet,
    private jwt: JwtService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.jwt.check();
    this.initializeForm();
    this.setupClientChangeListener();
    this.setupReportTypeChangeListener();
  }

  initializeForm() {
    this.collateralsForm = this.fb.group({
      client: [null, Validators.required],
      to: [null, Validators.required],
      year: [null],
      docType: [null, Validators.required],
      reportType: [null],
      reportPeriod: [null],
      filename: [null, Validators.required],
      uploadedFile: [null, Validators.required],
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];
      const validExtensions = [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'mp4',
        'pdf',
        'doc',
        'docx',
        'xls',
        'xlsx',
      ];
      const maxSizeInMB = 50;

      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      const fileSizeInMB = selectedFile.size / (1024 * 1024);

      if (
        fileExtension &&
        validExtensions.includes(fileExtension) &&
        fileSizeInMB <= maxSizeInMB
      ) {
        this.selectedFile = selectedFile;
        this.collateralsForm.get('uploadedFile')?.setValue(this.selectedFile);
      } else {
        if (!validExtensions.includes(fileExtension || '')) {
          alert(
            'Invalid file format. Please upload a file in one of the following formats: ' +
              validExtensions.join(', ')
          );
        } else if (fileSizeInMB > maxSizeInMB) {
          alert(
            'File size exceeds the 50MB limit. Please upload a smaller file.'
          );
        }
        input.value = '';
      }
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.collateralsForm.get('uploadedFile')?.setValue(null);
  }

  setupClientChangeListener() {
    this.collateralsForm.get('client')?.valueChanges.subscribe((clientId) => {
      if (clientId) {
        this.fetchDocumentArchiveDetails(clientId);
      } else {
        this.resetDropdownOptions();
      }
    });
  }

  fetchDocumentArchiveDetails(clientId: string) {
    const formData = new FormData();
    formData.append('client', clientId);

    this.api.documentarchivedetails(formData).subscribe(
      (response: any) => {
        this.to = response.channelDropdown || [];
        this.docTypes = response.docTypeDropdown || [];
        this.reportTypes = response.reportTypeDropdown || [];
        this.reportPeriod = response.reportPeriodDropdown || [];
        this.year = response.yearDropdown || [];
        this.dropdownOptions = {
          to: this.to,
          docType: this.docTypes,
          reportType: this.reportTypes,
          reportPeriod: this.reportPeriod,
          year: this.year,
          quarterDropdown: response.quarterDropdown || [],
          monthlyDropdown: response.monthlyDropdown || [],
        };
      },
      (error) => {
        this.snackbar.open('Failed to load archive details', 'Close', {
          duration: 3000,
        });
        this.resetDropdownOptions();
      }
    );
  }

  resetDropdownOptions() {
    this.to = [];
    this.docTypes = [];
    this.reportTypes = [];
    this.reportPeriod = [];
    this.year = [];
    this.dropdownOptions = {
      to: [],
      docType: [],
      reportType: [],
      reportPeriod: [],
      year: [],
      quarterDropdown: [],
      monthlyDropdown: [],
    };
  }

  onUpload() {
    this.collateralsForm.markAllAsTouched();

    if (this.collateralsForm.valid) {
      const formData = new FormData();
      const docType = this.collateralsForm.get('docType')?.value;

      formData.append('fileName', this.collateralsForm.get('filename')?.value);
      formData.append(
        'uploadedFile',
        this.collateralsForm.get('uploadedFile')?.value
      );
      formData.append('client', this.collateralsForm.get('client')?.value);
      formData.append('channel', this.collateralsForm.get('to')?.value);
      formData.append('docType', docType);
      formData.append('commit', 'yes');

      if (docType === 'Reports') {
        formData.append(
          'reportType',
          this.collateralsForm.get('reportType')?.value || ''
        );
        formData.append(
          'reportPeriod',
          this.collateralsForm.get('reportPeriod')?.value || ''
        );
        formData.append('year', this.collateralsForm.get('year')?.value || '');
      }
      this.api.documentarchivecreate(formData).subscribe(
        (response) => {
          alert('Details updated successfully!');
          window.location.reload();
        },
        (error) => {
          alert('Kindly check all the fields and try again.');
        }
      );
    }else {
      this.logFormDetails();
      alert('Kindly check all the fields and try again.');
    }
  }

  private logFormDetails() {
    for (const controlName in this.collateralsForm.controls) {
      const control = this.collateralsForm.get(controlName);
    }
  }

  setupReportTypeChangeListener() {
    this.collateralsForm
      .get('reportType')
      ?.valueChanges.subscribe((reportType) => {
        const reportTypeIndex =
          this.dropdownOptions.reportType.indexOf(reportType);

        if (reportTypeIndex === 0) {
          this.dropdownOptions.reportPeriod = [];
        } else if (reportTypeIndex === 1) {
          this.dropdownOptions.reportPeriod =
            this.dropdownOptions.quarterDropdown;
        } else if (reportTypeIndex === 2) {
          this.dropdownOptions.reportPeriod =
            this.dropdownOptions.monthlyDropdown;
        } else {
          this.dropdownOptions.reportPeriod = [];
        }

        this.collateralsForm.get('reportPeriod')?.reset();
      });
  }

  shouldShowReportPeriod(): boolean {
    const reportType = this.collateralsForm.get('reportType')?.value;
    return this.dropdownOptions.reportType.indexOf(reportType) !== 0;
  }
}

@Component({
  selector: 'uploaddocument-response',
  templateUrl: './uploaddocument-response.html',
})
export class UploaddocumentResponse {
  constructor(
    private _bottomSheetRef: MatBottomSheetRef<UploaddocumentComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: []
  ) {}
}
